import React, { useState, useEffect } from "react";
import { Search, Plus, Edit2, Ban, CheckCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import "./AdminsPage.css";
import AddAdminModal from "./AddAdminModal";
import cacheStorage from '../../../utils/cacheStorage';

const initialMockAdmins = [
  { id: "ADM-001", name: "Aysel Mammadova", email: "aysel.m@smartcargo.solutions", status: "active" },
  { id: "ADM-002", name: "Rashad Aliyev", email: "rashad.a@smartcargo.solutions", status: "active" },
  { id: "ADM-003", name: "Nigar Hasanova", email: "nigar.h@smartcargo.solutions", status: "blocked" },
];

export default function AdminsPage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [admins, setAdmins] = useState(initialMockAdmins);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);

  useEffect(() => {
    try {
      const token = cacheStorage.getItem("id_token");
      if (token) {
        const decoded = jwtDecode(token);
        const userRole = decoded["https://SmartCargo/roles"]?.[0];
        if (userRole !== "master admin") {
          navigate("/admin");
        }
      } else {
        navigate("/");
      }
    } catch (error) {
      console.error("Failed to decode token", error);
      navigate("/");
    }
  }, [navigate]);

  const handleAddSuccess = (newAdminData) => {
    if (editingAdmin) {
      setAdmins(admins.map(admin =>
        admin.id === editingAdmin.id
          ? { ...admin, name: newAdminData.fullName, email: newAdminData.email }
          : admin
      ));
      setEditingAdmin(null);
    } else {
      const newIdNum = admins.length + 1;
      const newId = `ADM-${String(newIdNum).padStart(3, "0")}`;

      const newAdmin = {
        id: newId,
        name: newAdminData.fullName,
        email: newAdminData.email,
        status: "active",
      };

      setAdmins([...admins, newAdmin]);
    }
  };

  const handleEdit = (admin) => {
    setEditingAdmin(admin);
    setIsModalOpen(true);
  };

  const toggleStatus = (id) => {
    setAdmins(
      admins.map((admin) =>
        admin.id === id
          ? { ...admin, status: admin.status === "active" ? "blocked" : "active" }
          : admin
      )
    );
  };

  const filteredAdmins = admins.filter(
    (admin) =>
      admin.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      admin.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      admin.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="admins-list-page">
      <div className="page-header">
        <div className="page-title-group">
          <h1>Admins</h1>
          <p>Manage administrator accounts</p>
        </div>
      </div>

      <div className="controls-bar">
        <div className="search-box">
          <Search size={20} className="search-icon" />
          <input
            type="text"
            placeholder="Search admins by name, ID, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="action-buttons">
          <button className="btn-primary" onClick={() => { setEditingAdmin(null); setIsModalOpen(true); }}>
            <Plus size={18} />
            Add Admin
          </button>
        </div>
      </div>

      <div className="table-container">
        <table className="admins-table">
          <thead>
            <tr>
              <th>Admin ID</th>
              <th>Name</th>
              <th>Contact</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAdmins.map((admin) => (
              <tr key={admin.id}>
                <td className="col-id">{admin.id}</td>
                <td className="col-name">
                  <div className="avatar-name">
                    <div className="avatar-circle">{admin.name.charAt(0)}</div>
                    <div className="name-text">{admin.name}</div>
                  </div>
                </td>
                <td className="col-contact">
                  <div className="contact-info">
                    <div className="email">{admin.email}</div>
                  </div>
                </td>
                <td className="col-status">
                  <span className={`status-badge ${admin.status}`}>
                    {admin.status.charAt(0).toUpperCase() + admin.status.slice(1)}
                  </span>
                </td>
                <td className="col-actions">
                  <div className="actions-cell">
                    <button className="action-btn" onClick={() => handleEdit(admin)} title="Edit">
                      <Edit2 size={18} />
                    </button>
                    {admin.status === "active" ? (
                      <button className="action-btn block-btn" onClick={() => toggleStatus(admin.id)} title="Block">
                        <Ban size={18} />
                      </button>
                    ) : (
                      <button className="action-btn unblock-btn" onClick={() => toggleStatus(admin.id)} title="Unblock">
                        <CheckCircle size={18} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {filteredAdmins.length === 0 && (
                <tr>
                  <td colSpan="5" style={{ textAlign: "center", padding: "1rem" }}>No admins found.</td>
                </tr>
            )}
          </tbody>
        </table>
      </div>

      <AddAdminModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditingAdmin(null); }}
        onAddSuccess={handleAddSuccess}
        editingAdmin={editingAdmin}
      />
    </div>
  );
}
